import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

const CATEGORIES = [
  'Vegetables & Fruits',
  'Meat & Poultry',
  'Seafood',
  'Dairy Products',
  'Spices & Condiments',
  'Grains & Oils',
  'Grains & Flour',
  'Dry Fruits & Nuts',
  'Beverages',
  'Organic Produce',
  'Packaging & Supplies',
];

const supplierService = {
  categories: CATEGORIES,

  /**
   * GET /api/inventory/suppliers
   * Supports: search, category, status, branchId (passed as query params via pageable)
   */
  async getAll(filters = {}) {
    const params = {};
    if (filters.search)   params.search   = filters.search;
    if (filters.category && filters.category !== 'all') params.category = filters.category;
    if (filters.status   && filters.status !== 'all')   params.status   = filters.status;
    if (filters.branchId) params.branchId = filters.branchId;

    // Default pagination — fetch a large page so the client-side list works
    params.page = 0;
    params.size = 200;

    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_ALL, { params });

    // The API returns a Spring Page object; unwrap the content array
    const data = response?.data ?? response;
    return Array.isArray(data) ? data : (data?.content ?? []);
  },

  /**
   * GET /api/inventory/suppliers/{id}
   */
  async getById(id) {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_BY_ID(id));
    return response?.data ?? response;
  },

  /**
   * POST /api/inventory/suppliers
   */
  async create(data) {
    const response = await apiClient.post(ENDPOINTS.SUPPLIERS.CREATE, data);
    return response?.data ?? response;
  },

  /**
   * PUT /api/inventory/suppliers/{id}
   */
  async update(id, data) {
    const response = await apiClient.put(ENDPOINTS.SUPPLIERS.UPDATE(id), data);
    return response?.data ?? response;
  },

  /**
   * DELETE /api/inventory/suppliers/{id}
   */
  async delete(id) {
    const response = await apiClient.delete(ENDPOINTS.SUPPLIERS.DELETE(id));
    return response?.data ?? response;
  },

  /**
   * PUT /api/inventory/suppliers/{id}/toggle-status
   */
  async toggleStatus(id) {
    const response = await apiClient.put(ENDPOINTS.SUPPLIERS.TOGGLE_STATUS(id));
    return response?.data ?? response;
  },

  /**
   * GET /api/inventory/suppliers/stats
   */
  async getStats() {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_STATS);
    return response?.data ?? response;
  },

  /**
   * GET /api/inventory/suppliers/{id}/orders
   * Returns: [{ poNumber, orderDate, itemCount, totalAmount, status }]
   */
  async getOrderHistory(supplierId) {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_ORDERS(supplierId));
    const data = response?.data ?? response;
    // Normalise field names to what OrdersTab expects: id, date, items, total, status
    const list = Array.isArray(data) ? data : [];
    return list.map((o) => ({
      id:     o.poNumber   ?? o.id,
      date:   o.orderDate  ?? o.date,
      items:  o.itemCount  ?? o.items  ?? 0,
      total:  o.totalAmount ?? o.total ?? 0,
      status: (o.status ?? '').toLowerCase(),
    }));
  },

  /**
   * GET /api/inventory/suppliers/{id}/price-list
   * Returns: [{ id, itemName, sku, uom, packSize, unitPrice, lastPrice, priceChange, lastUpdated }]
   */
  async getPriceList(supplierId) {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_PRICE_LIST(supplierId));
    const data = response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },

  /**
   * GET /api/inventory/suppliers/{id}/performance
   * Returns: { quality, delivery, cost, service, weightedScore,
   *            onTimeRate, orderAccuracy, avgLeadTime, totalOrders, totalValue }
   */
  async getPerformance(supplierId) {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_PERFORMANCE(supplierId));
    return response?.data ?? response;
  },

  /**
   * GET /api/inventory/suppliers/{id}/documents
   * Returns: [{ id, name, type, uploadDate, expiryDate, status }]
   */
  async getDocuments(supplierId) {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_DOCUMENTS(supplierId));
    const data = response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },

  /**
   * GET /api/inventory/suppliers/{id}/activity
   * Returns: [{ id, date, type, actor, description }]
   */
  async getActivityLog(supplierId) {
    const response = await apiClient.get(ENDPOINTS.SUPPLIERS.GET_ACTIVITY(supplierId));
    const data = response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },
};

export default supplierService;